import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Content,
  Divider,
  Heading,
  Menu,
  MenuItem,
  Stack,
} from '@kinesis/bungle';
import {
  CommunitySubSector,
  DisclosurePageDescriptor,
  DisclosurePageGroup,
  DisclosurePageKey,
  SourceType,
} from '@/types';
import { renderCommunitySubSector, renderSourceType } from '@/data/labels';

// Disclosure page navigation — the shared page/group framework, the same
// one suit's disclosure uses: list the pages `/summary` returns, grouped
// by their `group`, with the current page accented. Store-less; the page
// passes the page list, the current key, and a `pageLink` builder. It
// renders only what the API returns — today that's the Overview page;
// when the API lists more pages/groups they appear here with no change.
// Labels are typed to the current key/group sets, so adding a page/group
// is a compile-time prompt to label it.

const groupLabels: Record<DisclosurePageGroup, string> = {
  overview: 'Overview',
  'by-source': 'By source',
  'by-sector': 'By sector',
  methodology: 'Methodology',
  gpc: 'GPC',
};

// Fixed-key labels are literal; the two dynamic key forms — `source:<kebab>`
// and `sector:<kebab>` — are labelled by their source type / sub-sector. A
// static Record can't cover the dynamic keys, so this derives the label.
const pageLabel = (key: DisclosurePageKey): string => {
  switch (key) {
    case 'overview':
      return 'Overview';
    case 'location':
      return 'Emissions by location';
    case 'methodology':
      return 'Methodology';
    case 'sectors':
      return 'Emissions by sector';
    case 'gpc-summary':
      return 'Summary';
    case 'gpc-detail':
      return 'Detail';
    default:
      return key.startsWith('sector:')
        ? renderCommunitySubSector(
            key.slice('sector:'.length) as CommunitySubSector,
          )
        : renderSourceType(key.slice('source:'.length) as SourceType);
  }
};

type DisclosureSidebarProps = {
  pages: DisclosurePageDescriptor[];
  // The active page, or `undefined` before the landing page resolves — no item
  // is accented then (the sidebar only renders once `pages` is non-empty).
  pageKey: DisclosurePageKey | undefined;
  pageLink: (key: DisclosurePageKey) => string;
};

const DisclosureSidebar = (props: DisclosureSidebarProps) => {
  const { pages, pageKey, pageLink } = props;

  const grouped = useMemo(() => {
    const groups: {
      group: DisclosurePageGroup;
      items: DisclosurePageDescriptor[];
    }[] = [];
    for (const page of pages) {
      let existing = groups.find((g) => g.group === page.group);
      if (!existing) {
        existing = { group: page.group, items: [] };
        groups.push(existing);
      }
      existing.items.push(page);
    }
    return groups;
  }, [pages]);

  if (grouped.length === 0) return null;

  return (
    <Content
      background='white'
      minHeight='100%'
      sizing='fill-container'
      width={239}
      borderRight
    >
      <div style={{ position: 'sticky', top: 0 }}>
        <Content padding='medium'>
          <Stack space='small'>
            {grouped.map((group, index) => (
              <Stack space='small' key={group.group}>
                <Heading as='h4' size='small'>
                  {groupLabels[group.group]}
                </Heading>
                <Menu>
                  {group.items.map((item) => (
                    <MenuItem
                      key={item.key}
                      content={pageLabel(item.key)}
                      variant={pageKey === item.key ? 'accent' : 'default'}
                      as={Link}
                      to={pageLink(item.key)}
                    />
                  ))}
                </Menu>
                {index < grouped.length - 1 && <Divider />}
              </Stack>
            ))}
          </Stack>
        </Content>
      </div>
    </Content>
  );
};

export type { DisclosureSidebarProps };
export { DisclosureSidebar };
